import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { cloneDeep as _cloneDeep, compact, each, filter, find, forEach, isEmpty, map } from 'lodash-es';
import { UrlFormatter } from 'shared';
import { ParseService } from './parse.service';
import { languageHelper } from './language-helper.util';
import { createGoalModel } from './campaign-goals.util';

/**
 * Native port of the v1 `Campaign` factory (account-v1/src/core/api/campaign.js)
 * + its `CampaignCache` ($cacheFactory + the cache.js sliding `CacheExpiration`).
 * WS5 #10 Stage A inc 2s: replaces the `$injector.get('Campaign')` grabs in
 * ft-campaign-goals-only / ft-campaign-prestay / ft-campaign-container / journeys.
 *
 * PARITY-LOAD-BEARING CACHE: v1 `$http.get(..., {cache: CampaignCache})` dedupes
 * same-URL campaign GETs (incl. in-flight promise sharing), and every mutator
 * (update/reorder/create-NO/activate/updateGoals) calls `CampaignCache.removeAll()`.
 * The replica keeps both behaviors — cache hits change /api request COUNTS, which
 * the record/replay gate keys on. Expiry is the v1 cache.js sliding window
 * VERBATIM: the WHOLE cache clears when not accessed (get/put/removeAll all
 * slide the timer) for 60s, swept every 10s ($interval → a zone-external
 * setInterval; v1's digest-every-10s side effect is deliberately NOT replicated).
 *
 * Response shaping (`setupCampaign`) ported verbatim: int-coercions, empty
 * `{item: []}` collections, languageHelper.sortByDefaultLanguage, the per-message
 * language upsert, goal/stayLength coercions. `angular.copy` → lodash cloneDeep
 * (plain JSON data here). `angular.forEach` over objects → `forEach` (same
 * (value, key) iteration).
 */
@Injectable({ providedIn: 'root' })
export class CampaignV1ApiService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly parse = inject(ParseService);

  // --- the CampaignCache replica (sliding 60s, 10s sweep) ---
  private readonly cache = new Map<string, Promise<any>>();
  private cacheAccessed = Date.now();
  private sweepId: any;
  private static readonly CACHE_TIMEOUT_MS = 60000;
  private static readonly CACHE_SWEEP_MS = 10000;

  constructor() {
    const zone = inject(NgZone);
    zone.runOutsideAngular(() => {
      this.sweepId = setInterval(() => {
        if (this.cacheAccessed + CampaignV1ApiService.CACHE_TIMEOUT_MS <= Date.now()) {
          this.cache.clear();
        }
      }, CampaignV1ApiService.CACHE_SWEEP_MS);
    });
  }

  /** Clears the sweep on injector destroy (TestBed/teardown hygiene — Angular 15: no DestroyRef). */
  ngOnDestroy(): void {
    clearInterval(this.sweepId);
  }

  private cacheRemoveAll(): void {
    this.cacheAccessed = Date.now(); // v1 proxies removeAll through reset() too
    this.cache.clear();
  }

  /** v1 `Campaign.get(model)` — cached GET + setupCampaign shaping. */
  get(model: any): Promise<any> {
    const url = UrlFormatter.format('/api/:propertyUuid/properties/layers/:layerName/campaigns/:id', model);
    this.cacheAccessed = Date.now();
    let pending = this.cache.get(url);
    if (!pending) {
      pending = firstValueFrom(this.http.get(url));
      this.cache.set(url, pending);
      pending.then(
        // v1's done() cache.put at RESPONSE time slid the CacheExpiration timer.
        () => { this.cacheAccessed = Date.now(); },
        // v1 $http EVICTS the entry when the request fails (angular.js done() →
        // cache.remove on non-success) — only successes stay cached.
        () => { if (this.cache.get(url) === pending) this.cache.delete(url); },
      );
    }
    // v1 cached the RAW response and re-ran transformResponse (a fresh JSON.parse)
    // per caller — every consumer got its OWN object. Clone-per-get reproduces that
    // isolation (the 2s adversarial verify caught the header/editor aliasing).
    return pending.then((data: any) => {
      const copy = _cloneDeep(data);
      this.setupCampaign(copy);
      return copy;
    });
  }

  /** v1 `Campaign.update(model, changes)` — poststay sends goals-only. */
  update(model: any, changes: any): Promise<any> {
    this.cacheRemoveAll();
    const changesPack = _cloneDeep(changes);
    const url = '/api/:propertyUuid/properties/layers/:layerName/campaigns/:id';
    const data =
      model.layerName == 'poststay'
        ? { goals: this.convertGoalsToApiFormat(changesPack.goals) }
        : this.convertCampaignToApiFormat(changesPack);
    return firstValueFrom(this.http.put(UrlFormatter.format(url, model), data)).then((response: any) => {
      this.setupCampaign(response);
      return response;
    });
  }

  /** v1 `Campaign.reorder(model, changes)` — changes = the campaign-uuid array. */
  reorder(model: any, changes: any): Promise<any> {
    this.cacheRemoveAll();
    return firstValueFrom(
      this.http.put(
        UrlFormatter.format('/api/:propertyUUID/properties/:propertyUUID/layers/:layerName/campaigns/reorder', model),
        changes,
      ),
    );
  }

  /** v1 `Campaign.create(model, changes)`. */
  create(model: any, changes: any): Promise<any> {
    const data = this.convertCampaignToApiFormat(_cloneDeep(changes));
    return firstValueFrom(
      this.http.post(
        UrlFormatter.format('/api/:propertyUUID/properties/:propertyUUID/layers/:layerName/campaigns/create', model),
        data,
      ),
    );
  }

  /** v1 `Campaign.activate(model)` — PUT with no body. */
  activate(model: any): Promise<any> {
    this.cacheRemoveAll();
    return firstValueFrom(
      this.http.put(
        UrlFormatter.format('/api/:propertyUuid/properties/layers/:layerName/campaigns/:id/activate/:isActive', model),
        null,
      ),
    );
  }

  /** v1 `Campaign.updateGoals(model, changes)` (no native consumer yet; kept verbatim). */
  updateGoals(model: any, changes: any): Promise<any> {
    this.cacheRemoveAll();
    return firstValueFrom(
      this.http.put(
        UrlFormatter.format('/api/:propertyUuid/properties/layers/:layerName/campaigns/:id/goals', model),
        changes,
      ),
    );
  }

  /** v1 `Campaign.emptyCampaign()`. */
  emptyCampaign(): any {
    const campaign: any = { criteria: {} };
    this.setupCampaignEmptyCollections(campaign);
    campaign.messages = this.parse.arrayOrDefault(campaign.messages);
    return campaign;
  }

  // ---- the verbatim setupCampaign shaping chain ----

  private setupCampaign(campaign: any): void {
    this.batchConvert(campaign, ['type', 'isActive'], (v: any) => this.parse.intOrDefault(v));
    campaign.criteria = campaign.criteria || {};
    this.setupCampaignEmptyCollections(campaign);
    languageHelper.sortByDefaultLanguage(campaign.languages);
    campaign.messages = this.parse.arrayOrDefault(campaign.messages);
    each(campaign.messages, (message: any) => {
      message.isActive = this.parse.bool(message.isActive);
      message.languages = this.parse.arrayOrDefault(message.languages);
      languageHelper.sortByDefaultLanguage(message.languages);
      each(campaign.languages.item, (language: any) => {
        const languageItem = find(message.languages, { code: language.code }) as any;
        if (languageItem) {
          languageItem.name = language.name;
        } else {
          message.languages.push({ name: language.name, code: language.code });
        }
      });
    });
    forEach(campaign.goals.item, (goal: any) => this.setupGoal(goal));
    forEach(campaign.criteria.stayLengths.item, (lengthOfStay: any) => this.setupLengthOfStay(lengthOfStay));
  }

  private setupGoal(goal: any): void {
    this.batchConvert(goal, ['clicks', 'signups', 'type', 'isActive'], (v: any) => this.parse.intOrDefault(v));
  }

  private setupLengthOfStay(lengthOfStay: any): void {
    this.batchConvert(lengthOfStay, ['minNights', 'maxNights'], (v: any) => this.parse.intOrDefault(v));
  }

  private setupCampaignEmptyCollections(campaign: any): void {
    this.setupEmptyCollections(campaign, ['languages', 'messages', 'goals']);
    this.setupEmptyCollections(campaign.criteria, [
      'roomTypes', 'rateCodes', 'dateRanges', 'languages', 'stayLengths', 'categories', 'secondaryCategories',
    ]);
  }

  private setupEmptyCollections(object: any, collectionNames: string[]): void {
    forEach(collectionNames, (collectionName: string) => {
      object[collectionName] = object[collectionName] || { item: [] };
    });
  }

  private batchConvert(object: any, properties: string[], converter: (v: any) => any): void {
    forEach(properties, (property: string) => {
      object[property] = converter(object[property]);
    });
  }

  private convertCampaignToApiFormat(campaign: any): any {
    if (!campaign) return;
    forEach(campaign.messages, (message: any) => {
      if (message.id) {
        message.messageUUID = message.id;
        delete message.id;
      }
      message.languages = filter(message.languages, (language: any) => language.text);
      message.languages = map(message.languages, (language: any) => ({
        text: language.text,
        languageCode: language.code,
      }));
    });
    campaign.messages = { item: campaign.messages };
    forEach(campaign.criteria, (criteria: any, key: string) => {
      campaign[key] = { item: filter(criteria.item, (item: any) => !isEmpty(item)) };
    });
    campaign.goals = this.convertGoalsToApiFormat(campaign.goals);
    delete campaign.criteria;
    return campaign;
  }

  private convertGoalsToApiFormat(goals: any): any {
    goals.item = compact(goals.item);
    goals.item = map(goals.item, (goal: any) => createGoalModel(goal, goal.type));
    return goals;
  }
}
